import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { getFullImageUrl } from '../../config/environment';
import './BannerSlider.css';

const BannerSlider = ({ 
  autoRotate = true, 
  rotateInterval = 5000,
  showIndicators = true,
  height = '400px'
}) => {
  const [sliders, setSliders] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  // Get current language (en, hi, etc.)
  const currentLanguage = i18n.language || 'en';

  useEffect(() => {
    loadSliders();
  }, [currentLanguage]); // ⭐ Reload when language changes

  // Auto-rotate sliders
  useEffect(() => {
    if (autoRotate && sliders.length > 1) {
      const slideInterval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % sliders.length);
      }, rotateInterval);
      return () => clearInterval(slideInterval);
    }
  }, [sliders, autoRotate, rotateInterval]);

  const loadSliders = async () => {
    setLoading(true);
    try {
      const data = await api.getActiveSliders();
      console.log('🎞️ Loaded sliders:', data);
      console.log('🌐 Current language:', currentLanguage);
      
      // ⭐ Filter sliders by current language
      const languageFilteredSliders = data?.filter(slider => {
        // If slider has no language specified, show it for all languages
        if (!slider.language || slider.language === 'all') {
          return true;
        }
        // Match slider language with current language
        return slider.language.toLowerCase() === currentLanguage.toLowerCase();
      }) || [];

      console.log('🎯 Filtered sliders for', currentLanguage, ':', languageFilteredSliders);
      
      setSliders(languageFilteredSliders);
      setCurrentSlide(0); // Reset to first slide when language changes
    } catch (error) {
      console.error('Error loading sliders:', error);
      setSliders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSliderClick = (slider) => {
    if (slider.linkUrl) {
      if (slider.linkUrl.startsWith('http')) {
        window.open(slider.linkUrl, '_blank', 'noopener,noreferrer');
      } else {
        navigate(slider.linkUrl);
      }
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % sliders.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + sliders.length) % sliders.length);
  };

  if (loading) {
    return (
      <div className="banner-slider-loading" style={{ height }}>
        <div className="banner-spinner"></div>
      </div>
    );
  }

  if (sliders.length === 0) {
    return null;
  }

  return (
    <div className="banner-slider">
      {sliders.map((slider, index) => (
        <div
          key={slider.id}
          className={`banner-slide ${index === currentSlide ? 'active' : ''}`}
          onClick={() => handleSliderClick(slider)}
          style={{
            backgroundImage: slider.imageUrl 
              ? `url(${getFullImageUrl(slider.imageUrl)})`
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            cursor: slider.linkUrl ? 'pointer' : 'default'
          }}
          title={slider.title}
          role="button"
          tabIndex={index === currentSlide ? 0 : -1}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleSliderClick(slider);
            }
          }}
        />
      ))}

      {/* Navigation Arrows */}
      {sliders.length > 1 && (
        <>
          <button 
            className="banner-arrow banner-arrow-left" 
            onClick={(e) => {
              e.stopPropagation();
              prevSlide();
            }}
            aria-label="Previous slide"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <button 
            className="banner-arrow banner-arrow-right" 
            onClick={(e) => {
              e.stopPropagation();
              nextSlide();
            }}
            aria-label="Next slide"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </>
      )}

      {/* Indicators */}
      {showIndicators && sliders.length > 1 && (
        <div className="banner-indicators">
          {sliders.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                goToSlide(index);
              }}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerSlider;
